import React, { useState, useEffect } from 'react';
import { Building2, Plus, Users, Settings, Trash2, ChevronDown, ChevronRight, Key } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { api } from '../../services/api';

interface OrgSetting {
  key: string;
  value: string;
  label: string;
  group: string;
  isSecret: boolean;
}

interface OrgMember {
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  org_role: string;
  total_tokens_used: number;
}

const OrganizationManagement: React.FC = () => {
  const { token } = useAuth();
  const { organizations, activeOrg, switchOrg, refreshOrgs } = useOrg();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [orgMembers, setOrgMembers] = useState<Record<string, OrgMember[]>>({});
  const [orgSettings, setOrgSettings] = useState<Record<string, OrgSetting[]>>({});
  const [showSettings, setShowSettings] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({ name: '', adminName: '', adminEmail: '', adminPassword: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadMembers = async (orgId: string) => {
    try {
      const data = await api.get(`/organizations/${orgId}/members`);
      if (data.success) setOrgMembers(prev => ({ ...prev, [orgId]: data.data }));
    } catch (e) { console.error(e); }
  };

  const loadSettings = async (orgId: string) => {
    try {
      const data = await api.get(`/organizations/${orgId}/settings`);
      if (data.success) setOrgSettings(prev => ({ ...prev, [orgId]: data.data }));
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    if (!createForm.name) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.post('/organizations', {
        name: createForm.name,
        adminName: createForm.adminName || undefined,
        adminEmail: createForm.adminEmail || undefined,
        adminPassword: createForm.adminPassword || undefined,
      });
      setMessage({ type: 'success', text: `Organization "${createForm.name}" created!` });
      setCreateForm({ name: '', adminName: '', adminEmail: '', adminPassword: '' });
      setShowCreateForm(false);
      await refreshOrgs();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (orgId: string, orgName: string) => {
    if (!window.confirm(`Delete organization "${orgName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/organizations/${orgId}`);
      setMessage({ type: 'success', text: `Organization "${orgName}" deleted.` });
      await refreshOrgs();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const handleSettingSave = async (orgId: string, key: string, value: string) => {
    try {
      await api.put(`/organizations/${orgId}/settings`, {
        settings: [{ key, value }]
      });
      await loadSettings(orgId);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const toggleOrg = (orgId: string) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      if (!orgMembers[orgId]) loadMembers(orgId);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-auto">
      <div className="p-8 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              Organization Management
            </h1>
            <p className="text-gray-500 mt-1">Create and manage organizations</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Organization
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4">Create New Organization</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                <input type="text" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Acme Corp" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                <input type="text" value={createForm.adminName} onChange={e => setCreateForm(f => ({ ...f, adminName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Org admin name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                <input type="email" value={createForm.adminEmail} onChange={e => setCreateForm(f => ({ ...f, adminEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="admin@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                <input type="password" value={createForm.adminPassword} onChange={e => setCreateForm(f => ({ ...f, adminPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Min 6 characters" />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={handleCreate} disabled={saving || !createForm.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors">
                {saving ? 'Creating...' : 'Create Organization'}
              </button>
              <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Organizations List */}
        <div className="space-y-3">
          {organizations.map((org: any) => (
            <div key={org.id} className={`bg-white rounded-xl border ${activeOrg?.id === org.id ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'} shadow-sm`}>
              {/* Org Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => toggleOrg(org.id)}>
                  {expandedOrg === org.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-semibold text-gray-900">{org.name}</div>
                    <div className="text-xs text-gray-500">
                      {org.member_count || 0} members · {org.plan_type || 'No plan'} · ID: {org.id.substring(0, 15)}...
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {activeOrg?.id !== org.id && (
                    <button onClick={() => switchOrg(org.id)}
                      className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors">
                      Switch
                    </button>
                  )}
                  {activeOrg?.id === org.id && (
                    <span className="px-3 py-1 text-xs bg-green-50 text-green-700 rounded-md font-medium">Active</span>
                  )}
                  <button onClick={() => { setShowSettings(showSettings === org.id ? null : org.id); if (!orgSettings[org.id]) loadSettings(org.id); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md" title="Settings">
                    <Key className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(org.id, org.name)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded: Members */}
              {expandedOrg === org.id && (
                <div className="border-t border-gray-100 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><Users className="w-4 h-4" /> Members</h4>
                  {orgMembers[org.id] ? (
                    <div className="space-y-1">
                      {orgMembers[org.id].map(m => (
                        <div key={m.user_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 text-sm">
                          <div>
                            <span className="font-medium text-gray-900">{m.user_name}</span>
                            <span className="text-gray-500 ml-2">{m.user_email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{m.org_role}</span>
                            <span className="text-xs text-gray-400">{Number(m.total_tokens_used).toLocaleString()} tokens</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Fetching team members...</p>
                  )}
                </div>
              )}

              {/* Settings Panel */}
              {showSettings === org.id && (
                <div className="border-t border-gray-100 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1"><Settings className="w-4 h-4" /> API Keys & Settings</h4>
                  {orgSettings[org.id] ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {orgSettings[org.id].map(s => (
                        <div key={s.key} className="text-sm">
                          <label className="block text-xs font-medium text-gray-600 mb-1">{s.label} <span className="text-gray-400">({s.group})</span></label>
                          <div className="flex gap-1">
                            <input
                              type={s.isSecret ? 'password' : 'text'}
                              defaultValue={s.value === '••••••••' ? '' : s.value}
                              placeholder={s.isSecret ? '••••••••' : 'Not set'}
                              className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500"
                              onBlur={e => { if (e.target.value && e.target.value !== s.value) handleSettingSave(org.id, s.key, e.target.value); }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Fetching organization settings...</p>
                  )}
                </div>
              )}
            </div>
          ))}

          {organizations.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No organizations yet. Create one to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationManagement;
